import { useEffect, useState } from "react";
import {
  Box, Chip, Typography, IconButton, Tooltip,
  TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, CircularProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Swal from "sweetalert2";
import {
  listApprovalRequestsAction,
  approveRequestAction,
  rejectRequestAction,
} from "../../Actions/ApprovalActions";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import CustomSnackbar from "../Common/snackBar";
import { formatDateTime } from "../Common/DateFormat";
import DataTable from "../../components/DataTable/DataTable";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import { decrypt, decryptedData, encrypt } from "../../utils/encryptDecrypt";


// const StatusChip = ({ status }) => {
//   const map = {
//     PENDING: { label: "Pending", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
//     APPROVED: { label: "Approved", color: "#16a34a", bg: "rgba(22,163,74,0.12)" },
//     REJECTED: { label: "Rejected", color: "#dc2626", bg: "rgba(220,38,38,0.12)" },
//   };
//   const s = map[status] || map.PENDING;
//   return (
//     <Chip
//       label={s.label}
//       size="small"
//       sx={{ fontWeight: 700, color: s.color, bgcolor: s.bg, border: `1px solid ${s.color}33` }}
//     />
//   );
// };

// ── Detail Dialog ──────────────────────────────────────────────────────────────
const DetailDialog = ({ open, onClose, row }) => {
  if (!row) return null;

  const comparisonData = row.comparison_data || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 700,
          bgcolor: "#22419e",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "18px",
            color: "#fff",
          }}
        >
          Request Detail — {row.id}
        </Typography>

        <IconButton
          onClick={onClose}
          sx={{
            color: "#fff",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.15)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, mt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[
            ["Entity", row.entity_name],
            ["Action", row.action_type],
            ["Status", row.status],
            [
              "Requested By",
              `${row?.user?.firstName
                ? decryptedData(row.user.firstName)
                : ""
                } ${row?.user?.lastName
                  ? decryptedData(row.user.lastName)
                  : ""
                }`.trim() || "—",
            ],
            // ["Entity ID", row.entity_id || "—"],
            [
              "Submitted",
              row.createdAt ? formatDateTime(row.createdAt) : "—",
            ],
            [
              "Approved At",
              row.approved_at
                ? formatDateTime(row.approved_at)
                : "—",
            ],

            ...(row.status?.toLowerCase() === "rejected"
              ? [["Rejected Reason", row.rejection_reason || "—"]]
              : []),
          ].map(([label, val]) => (
            <Box key={label} sx={{ display: "flex", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  minWidth: 140,
                  fontWeight: 600,
                  color: "#555",
                }}
              >
                {label}:
              </Typography>

              <Typography variant="body2">
                {String(val)}
              </Typography>
            </Box>
          ))}

          {/* ✅ Highlighted Changed Fields */}
          {Object.keys(comparisonData).length > 0 && (
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                {Object.entries(comparisonData).flatMap(([sectionName, sectionFields]) => {

                  // ✅ HANDLE NORMAL FORMAT (PLAN)
                  const isNormalField =
                    sectionFields &&
                    typeof sectionFields === "object" &&
                    ("old" in sectionFields || "new" in sectionFields);

                  // ✅ HANDLE NESTED FORMAT (TENANT)
                  const fieldsToRender = isNormalField
                    ? [[sectionName, sectionFields]]
                    : Object.entries(sectionFields || {});

                  return fieldsToRender.map(([fieldName, fieldData]) => (
                    <Box
                      key={fieldName}
                      sx={{
                        p: 2,
                        background:
                          "linear-gradient(135deg, #fff8e1 0%, #fff3cd 100%)",
                        border: "1px solid #ffb74d",
                        borderLeft: "5px solid #ff9800",
                        borderRadius: "10px",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          boxShadow:
                            "0 4px 12px rgba(255,152,0,0.15)",
                        },
                      }}
                    >
                      {/* HEADER */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "15px",
                            color: "#d97706",
                          }}
                        >
                          {fieldName
                            .replace(/([A-Z])/g, " $1")
                            .replace(/^./, str => str.toUpperCase())
                            .trim()}
                        </Typography>

                        <Box
                          sx={{
                            fontSize: "11px",
                            fontWeight: 700,
                            color: "#fff",
                            background: "#ff9800",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "4px",
                          }}
                        >
                          CHANGED
                        </Box>
                      </Box>

                      {/* VALUES */}
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 2,
                        }}
                      >
                        {/* OLD */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "#666",
                              mb: 0.75,
                              fontWeight: 700,
                              textTransform: "uppercase",
                            }}
                          >
                            Current Value
                          </Typography>

                          <Box
                            sx={{
                              p: 1.5,
                              background: "#fafafa",
                              border: "1px solid #e0e0e0",
                              borderLeft: "4px solid #9e9e9e",
                              borderRadius: "6px",
                              fontSize: "13px",
                              color: "#333",
                              minHeight: "42px",
                              display: "flex",
                              alignItems: "center",
                              wordBreak: "break-word",
                              fontFamily: "monospace",
                            }}
                          >
                            {fieldData?.old === null ||
                              fieldData?.old === undefined ||
                              fieldData?.old === "" ? (
                              <span
                                style={{
                                  color: "#999",
                                  fontStyle: "italic",
                                }}
                              >
                                —
                              </span>
                            ) : typeof fieldData.old === "object" ? (
                              JSON.stringify(fieldData.old)
                            ) : (
                              String(fieldData.old)
                            )}
                          </Box>
                        </Box>

                        {/* NEW */}
                        <Box>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: "#2e7d32",
                              mb: 0.75,
                              fontWeight: 700,
                              textTransform: "uppercase",
                            }}
                          >
                            Requested Value
                          </Typography>

                          <Box
                            sx={{
                              p: 1.5,
                              background: "#f1f8e9",
                              border: "1px solid #81c784",
                              borderLeft: "4px solid #4caf50",
                              borderRadius: "6px",
                              fontSize: "13px",
                              color: "#1b5e20",
                              fontWeight: 600,
                              minHeight: "42px",
                              display: "flex",
                              alignItems: "center",
                              wordBreak: "break-word",
                              fontFamily: "monospace",
                            }}
                          >
                            {fieldData?.new === null ||
                              fieldData?.new === undefined ||
                              fieldData?.new === "" ? (
                              <span
                                style={{
                                  color: "#999",
                                  fontStyle: "italic",
                                }}
                              >
                                —
                              </span>
                            ) : typeof fieldData.new === "object" ? (
                              JSON.stringify(fieldData.new)
                            ) : (
                              String(fieldData.new)
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ));
                })}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Reject Reason Dialog ───────────────────────────────────────────────────────
const RejectDialog = ({ open, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState("");
  const handleConfirm = () => { onConfirm(reason); setReason(""); };
  const handleClose = () => { setReason(""); onClose(); };
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Reject Request</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth multiline rows={3} size="small"
          label="Rejection Reason"
          value={reason} onChange={e => setReason(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" size="small">Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          size="small"
          disabled={loading || !reason.trim()}
          startIcon={
            loading
              ? <CircularProgress size={14} color="inherit" />
              : null
          }
        >
          Confirm Reject
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────────
const ApprovalRequests = () => {
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [detailRow, setDetailRow] = useState(null);
  const [rejectRow, setRejectRow] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await listApprovalRequestsAction(page, pageSize, statusFilter, searchValue);
      // The decrypt interceptor nests data: res.data may be the full response object
      const payload = res?.data;
      const rowsData = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
      setRows(rowsData);
      setTotalRecords(res?.totalRecords || payload?.totalRecords || 0);
    } catch (e) {
      setSnackbar({ open: true, message: e?.response?.data?.message || "Failed to fetch", severity: "error" });
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
  },
    [page, pageSize, statusFilter, searchValue]);

  // ── Approve ────────────────────────────────────────────────────────────────
  const handleApprove = async (row) => {
    const result = await Swal.fire({
      title: "Approve Request?",
      html: `<p>Approve <strong>${row.action_type}</strong> request for <strong>${row.entity_name}</strong>?</p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#16a34a",
      cancelButtonColor: "#777",
      confirmButtonText: "Yes, Approve",
    });
    if (!result.isConfirmed) return;
    setActionLoading(true);
    try {
      await approveRequestAction(row.id);
      setSnackbar({ open: true, message: "Request approved successfully", severity: "success" });
      fetchData(); // refresh table + this also triggers ApprovalBell re-fetch via notification
    } catch (e) {
      setSnackbar({ open: true, message: e?.response?.data?.message || "Approve failed", severity: "error" });
    } finally { setActionLoading(false); }
  };

  // ── Reject ─────────────────────────────────────────────────────────────────
  const handleRejectConfirm = async (reason) => {
    setActionLoading(true);
    try {
      await rejectRequestAction(rejectRow.id, reason);
      setRejectRow(null);
      setSnackbar({ open: true, message: "Request rejected", severity: "success" });
      fetchData();
    } catch (e) {
      setSnackbar({ open: true, message: e?.response?.data?.message || "Reject failed", severity: "error" });
    } finally { setActionLoading(false); }
  };

  // ── Table action handler (used by DataTable onAction) ──────────────────────
  const handleTableAction = (action, row) => {
    switch (action) {
      case "view":
        setDetailRow(row);
        break;

      case "approve":
        if (row.status === "PENDING") {
          handleApprove(row);
        }
        break;

      case "reject":
        if (row.status === "PENDING") {
          setRejectRow(row);
        }
        break;

      default:
        break;
    }
  };

  // ── Columns ────────────────────────────────────────────────────────────────
  const columns = [
    // {
    //   field: "id", headerName: "#", minWidth: 60, flex: 0.4,
    //   renderCell: ({ row }) => <Typography variant="body2" sx={{ fontWeight: 600 }}>#{row.id}</Typography>,
    // },
    {
      field: "entity_name", headerName: "Entity", minWidth: 130, flex: 1,
      renderCell: ({ row }) => (
        <Chip label={row.entity_name} size="small"
          sx={{ bgcolor: "rgba(34,65,158,0.1)", color: "#22419e", fontWeight: 600 }} />
      ),
    },
    {
      field: "action_type", headerName: "Action", minWidth: 100, flex: 0.8,
      renderCell: ({ row }) => {
        const c = { CREATE: "#16a34a", UPDATE: "#f59e0b", DELETE: "#dc2626", WORKFLOW: "#7c3aed", STATUS_UPDATE: "#2563eb" };
        return (
          <Chip label={row.action_type} size="small"
            sx={{
              fontWeight: 700, color: c[row.action_type] || "#555",
              bgcolor: `${c[row.action_type]}18`, border: `1px solid ${c[row.action_type]}33`
            }} />
        );
      },
    },
    {
      field: "user",
      headerName: "Requested By",
      minWidth: 180,
      flex: 1,
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {row?.user?.firstName ? decryptedData(row.user.firstName) : ""}
          {" "}
          {row?.user?.lastName ? decryptedData(row.user.lastName) : ""}
        </Typography>
      )
    },
    // {
    //   field: "entity_id", headerName: "Record ID", minWidth: 90, flex: 0.7,
    //   renderCell: ({ row }) => <Typography variant="body2">{row.entity_id || "—"}</Typography>,
    // },
    {
      field: "role",
      headerName: "Role",
      minWidth: 120,
      flex: 1,
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {row.role?.roleName || "-"}
        </Typography>
      ),
    },
    // {
    //   field: "status", headerName: "Status", minWidth: 110, flex: 0.8,
    //   renderCell: ({ row }) => <StatusChip status={row.status} />,
    // },
    {
      field: "created_at", headerName: "Submitted", minWidth: 150, flex: 1.2,
      renderCell: ({ row }) => (
        <Typography variant="body2" sx={{ color: "#666" }}>
          {row.createdAt ? formatDateTime(row.createdAt, true) : "—"}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 0.8,
      renderCell: ({ row }) => {
        const statusMap = {
          PENDING: {
            label: "Pending",
            color: "#f59e0b",
            bg: "rgba(245,158,11,0.12)",
          },
          APPROVED: {
            label: "Approved",
            color: "#16a34a",
            bg: "rgba(22,163,74,0.12)",
          },
          REJECTED: {
            label: "Rejected",
            color: "#dc2626",
            bg: "rgba(220,38,38,0.12)",
          },
        };

        const s = statusMap[row.status] || statusMap.PENDING;

        return (
          <Chip
            label={s.label}
            size="small"
            sx={{
              fontWeight: 700,
              color: s.color,
              bgcolor: s.bg,
              border: `1px solid ${s.color}33`,
            }}
          />
        );
      },
    },
  ];

  const filters = [
    { type: "search", name: "search", placeholder: "Search entity..." },
    {
      type: "select", name: "status", label: "Status", defaultValue: "",
      options: [
        { label: "Pending", value: "PENDING" },
        { label: "Approved", value: "APPROVED" },
        { label: "Rejected", value: "REJECTED" },
        { label: "All", value: "" },
      ],
    },
  ];

  const handleFilterApply = (values) => {
    setSearchValue(values.search || "");
    setStatusFilter(values.status || "");
    setPage(1);
  };

  const handleFilterReset = () => {
    setSearchValue("");
    setStatusFilter("");
    setPage(1);
  };

  // ── Stats bar ──────────────────────────────────────────────────────────────
  const pendingCount = rows.filter(r => r.status === "PENDING").length;

  return (
    <div id="kt_content_container">
      <BreadcrumbsComponent />

      {/* Stats bar
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        {[
          { label: "Pending", color: "#f59e0b", bg: "rgba(245,158,11,0.1)", filter: "PENDING" },
          { label: "Approved", color: "#16a34a", bg: "rgba(22,163,74,0.1)", filter: "APPROVED" },
          { label: "Rejected", color: "#dc2626", bg: "rgba(220,38,38,0.1)", filter: "REJECTED" },
        ].map(s => (
          <Box
            key={s.label}
            onClick={() => { setStatusFilter(s.filter); setPage(1); }}
            sx={{
              cursor: "pointer", px: 3, py: 1.5, borderRadius: 2,
              bgcolor: statusFilter === s.filter ? s.bg : "#fff",
              border: `1.5px solid ${statusFilter === s.filter ? s.color : "#e5e7eb"}`,
              display: "flex", alignItems: "center", gap: 1,
              transition: "all 0.2s",
              "&:hover": { bgcolor: s.bg, borderColor: s.color },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: s.color }}>{s.label}</Typography>
          </Box>
        ))}
      </Box> */}

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        totalCount={totalRecords}
        page={page}
        pageSize={pageSize}
        filters={filters}
        filterValues={{ search: searchValue, status: statusFilter }}
        onFilterApply={handleFilterApply}
        onFilterReset={handleFilterReset}
        onPageChange={(p) => setPage(p)}
        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
        onAction={handleTableAction}
        actions={{
          view: true, approve: true, reject: true,
        }}
      />

      {/* Detail Dialog */}
      <DetailDialog open={Boolean(detailRow)} onClose={() => setDetailRow(null)} row={detailRow} />

      {/* Reject Dialog */}
      <RejectDialog
        open={Boolean(rejectRow)}
        onClose={() => setRejectRow(null)}
        onConfirm={handleRejectConfirm}
        loading={actionLoading}
      />

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      />
    </div>
  );
};

export default ApprovalRequests;
